import {
  formatTranspose,
  suggestCapo,
  type Accidentals,
  type CapoSuggestion,
  type DisplayOptions,
  type Notation,
} from '@/core/theory/display';
import { MinusIcon, PlusIcon } from '@/ui/icons';
import type { Complexity } from '@/core/theory/chord';
import { Segmented } from '@/ui/Segmented';
import { Switch } from '@/ui/Switch';
import { COMPLEXITY_OPTIONS } from './complexity';
import styles from './KeyCapoPanel.module.css';

interface KeyCapoPanelProps {
  options: DisplayOptions;
  showCapo: boolean;
  onTranspose: (semitones: number) => void;
  onCapo: (fret: number) => void;
  onNotation: (notation: Notation) => void;
  onAccidentals: (accidentals: Accidentals) => void;
  complexity: Complexity;
  onComplexity: (complexity: Complexity) => void;
  /** Pitch-shift the recording with the transpose (F3.3). */
  shiftAudio: boolean;
  /** Omitted when the recording can't be shifted (YouTube songs): the switch is hidden. */
  onShiftAudio?: (on: boolean) => void;
  /** Capo advice from the song's chord shapes (F5.3); falls back to the key-based rule. */
  suggestion?: CapoSuggestion;
  /** Easy mode (F5.4): plain triads and the easiest shapes. */
  easyMode: boolean;
  onEasyMode: (on: boolean) => void;
}

const NOTATIONS: { value: Notation; label: string }[] = [
  { value: 'letters', label: 'Letters (C D E)' },
  { value: 'german', label: 'German (H for B)' },
  { value: 'solfege', label: 'Solfège (Do Re Mi)' },
  { value: 'roman', label: 'Roman numerals' },
  { value: 'nashville', label: 'Nashville numbers' },
];

const ACCIDENTALS = [
  { value: 'auto' as const, label: 'Auto', ariaLabel: 'Auto: spell by key' },
  { value: 'sharps' as const, label: '♯', ariaLabel: 'Always sharps' },
  { value: 'flats' as const, label: '♭', ariaLabel: 'Always flats' },
];

function Stepper({
  value,
  minWidth,
  downLabel,
  upLabel,
  onDown,
  onUp,
}: {
  value: string;
  minWidth: number;
  downLabel: string;
  upLabel: string;
  onDown: () => void;
  onUp: () => void;
}) {
  return (
    <div className={styles.stepper}>
      <button type="button" className={styles.stepButton} aria-label={downLabel} onClick={onDown}>
        <MinusIcon size={14} />
      </button>
      <span className={styles.stepValue} style={{ minWidth }} aria-live="polite">
        {value}
      </span>
      <button type="button" className={styles.stepButton} aria-label={upLabel} onClick={onUp}>
        <PlusIcon size={14} />
      </button>
    </div>
  );
}

/** Transpose, capo, capo suggestion, notation and spelling (F5.1–F5.6; design: Key & capo card). */
export function KeyCapoPanel({
  options,
  showCapo,
  onTranspose,
  onCapo,
  onNotation,
  onAccidentals,
  complexity,
  onComplexity,
  shiftAudio,
  onShiftAudio,
  suggestion: songSuggestion,
  easyMode,
  onEasyMode,
}: KeyCapoPanelProps) {
  const { transpose, capo } = options;
  const suggestion = songSuggestion ?? suggestCapo(options);
  const suggestText =
    suggestion.capo === 0
      ? 'Open shapes work here — no capo needed.'
      : `Suggested: capo ${suggestion.capo}, play ${suggestion.shapeKey} shapes.`;

  return (
    <section aria-label="Key and capo" className={styles.card}>
      <div className={styles.row}>
        <span className={styles.rowLabel}>Transpose</span>
        <div className={styles.controls}>
          <Stepper
            value={formatTranspose(transpose)}
            minWidth={44}
            downLabel="Transpose down a semitone"
            upLabel="Transpose up a semitone"
            onDown={() => {
              onTranspose(transpose - 1);
            }}
            onUp={() => {
              onTranspose(transpose + 1);
            }}
          />
          <button
            type="button"
            className={styles.reset}
            disabled={transpose === 0}
            onClick={() => {
              onTranspose(0);
            }}
          >
            Reset
          </button>
        </div>
      </div>
      {onShiftAudio && (
        <div className={styles.switchRow}>
          <span id="shift-audio-label" className={styles.switchLabel}>
            Shift the recording too, so it plays in the new key
          </span>
          <Switch labelledBy="shift-audio-label" checked={shiftAudio} onChange={onShiftAudio} />
        </div>
      )}
      {showCapo && (
        <>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Capo</span>
            <Stepper
              value={capo === 0 ? 'No capo' : `Fret ${capo}`}
              minWidth={76}
              downLabel="Move capo down a fret"
              upLabel="Move capo up a fret"
              onDown={() => {
                onCapo(capo - 1);
              }}
              onUp={() => {
                onCapo(capo + 1);
              }}
            />
          </div>
          <div className={styles.suggestion}>
            <span>{suggestText}</span>
            {suggestion.capo !== capo && (
              <button
                type="button"
                className={styles.use}
                onClick={() => {
                  onCapo(suggestion.capo);
                }}
              >
                Use
              </button>
            )}
          </div>
        </>
      )}

      {/* Set-and-forget options fold away so transpose and capo fit without scrolling. */}
      <details className={styles.more}>
        <summary className={styles.moreSummary}>More options</summary>
        <div className={styles.moreBody}>
          <div className={styles.notationRow}>
            <label className={styles.field}>
              Notation
              <select
                value={options.notation}
                onChange={(e) => {
                  onNotation(e.target.value as Notation);
                }}
              >
                {NOTATIONS.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.field}>
              <span>Sharps / flats</span>
              <Segmented
                label="Sharps or flats"
                options={ACCIDENTALS}
                value={options.accidentals}
                onChange={onAccidentals}
                stretch
              />
            </div>
          </div>
          <div className={styles.field}>
            <span>Chord detail</span>
            <Segmented
              label="Chord detail"
              options={COMPLEXITY_OPTIONS}
              value={easyMode ? 'simple' : complexity}
              onChange={onComplexity}
              stretch
            />
          </div>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={easyMode}
              onChange={(e) => {
                onEasyMode(e.target.checked);
              }}
            />
            Easy mode: plain chords and the easiest shapes
          </label>
        </div>
      </details>
    </section>
  );
}
